import React, { useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin as GoogleOAuthLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';

const inputCls =
  'w-full h-10 px-3.5 rounded-lg border border-charcoal/15 bg-warm-white text-sm focus:outline-none focus:border-forest focus:ring-2 focus:ring-sage/40';

/** The sign-in screen: the brand, one line of purpose, the Google button.
 *  Email sign-in stays one click away for accounts without Google. */
const GoogleLogin: React.FC = () => {
  const { login, loginWithPassword, isLoading } = useAuth();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [withEmail, setWithEmail] = useState(false);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await loginWithPassword(email, password);
    } catch {
      // AuthContext already alerted
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="relative min-h-screen flex flex-col items-center justify-center bg-warm-white px-4">
        <div className="w-full max-w-[340px] flex flex-col items-center text-center">
          <img src="/logo.png" alt="Bhuvana Fortune Greens" className="h-16 w-auto mb-6" />
          <h1 className="font-display text-[28px] font-semibold text-forest tracking-tight leading-tight">Campaign Portal</h1>
          <div className="w-10 h-[3px] bg-gold rounded-full mt-3 mb-4" />
          <p className="text-graphite text-[14px] mb-9">Every campaign, measured in site visits.</p>

          <GoogleOAuthLogin
            onSuccess={async (credentialResponse) => {
              if (credentialResponse.credential) {
                await login(credentialResponse.credential);
              }
            }}
            onError={() => {
              console.log('Login Failed');
            }}
            theme="outline"
            size="large"
            text="signin_with"
            shape="rectangular"
          />

          {withEmail ? (
            <form onSubmit={handlePasswordSubmit} className="w-full space-y-2.5 mt-8 pt-8 border-t border-charcoal/10">
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className={inputCls}
                autoFocus
                required
              />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={inputCls}
                required
              />
              <button
                type="submit"
                disabled={submitting || isLoading || !email || !password}
                className="w-full h-10 bg-forest text-warm-white rounded-lg text-sm font-semibold hover:bg-forest/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : (
            <button
              onClick={() => setWithEmail(true)}
              className="mt-6 text-[13px] font-semibold text-graphite hover:text-forest underline-offset-2 hover:underline"
            >
              Sign in with email instead
            </button>
          )}
        </div>
        <p className="absolute bottom-6 text-[11.5px] text-graphite/70">Bhuvana Fortune Greens · internal</p>
      </div>
    </GoogleOAuthProvider>
  );
};

export default GoogleLogin;
